import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toast } from "sonner";
import { type AxiosError } from "axios";
import { AppButton, AppInput, Divider, FigmaTypography } from "@libs/zapi-ui/custom";
import { CheckIcon } from "@libs/zapi-ui/assets";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useChangePassword } from "../services/useChangePassword";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

const NewPasswordSchema = z.string().min(8).max(32).regex(/[0-9]/).regex(/[A-Z]/);

export function ChangePasswordSection() {
  const { t } = useTranslation("settings");
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { mutateAsync: changePassword, isPending } = useChangePassword();
  const refetch = useMeStore((s) => s.refetch);

  const isLengthValid = form.newPassword.length >= 8;
  const hasNumber = /[0-9]/.test(form.newPassword);
  const hasCapital = /[A-Z]/.test(form.newPassword);
  const passwordsMatch = form.newPassword.length > 0 && form.newPassword === form.confirmPassword;
  const isValid =
    form.currentPassword.length > 0 && NewPasswordSchema.safeParse(form.newPassword).success && passwordsMatch;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    try {
      await changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast.success(t("password.changed"));
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      await refetch?.();
    } catch (err) {
      const message = (err as AxiosError<{ message: string }>)?.response?.data?.message ?? t("password.changeFailed");
      toast.error(message);
    }
  }

  function passwordInput(label: string, key: keyof typeof form, show: boolean, setShow: (v: boolean) => void) {
    return (
      <div className="relative w-[320px]">
        <AppInput
          label={label}
          type={show ? "text" : "password"}
          value={form[key]}
          onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value.trim() }))}
          disabled={isPending}
          fullWidth
          className="pr-12"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-0 bottom-0 h-[32px] flex items-center pr-3 text-[14px] font-medium"
        >
          {show ? t("common.hide") : t("common.show")}
        </button>
      </div>
    );
  }

  function criterion(label: string, met: boolean) {
    return (
      <div className="flex gap-2 items-center">
        <span
          className={cn(
            "w-[14px] h-[14px] rounded-full flex items-center justify-center",
            met ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
          )}
        >
          {met && <CheckIcon width={8} height={8} />}
        </span>
        <FigmaTypography variant="body-3" className={cn(met ? "text-successGreen" : "text-innoveraGrayscale-500")}>{label}</FigmaTypography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[18px] bg-white">
      <FigmaTypography variant="heading-3" color="zinc900">
        {t("password.heading")}
      </FigmaTypography>

      <Divider className="!bg-innoveraZinc-100" />

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {passwordInput(t("password.current"), "currentPassword", showCurrent, setShowCurrent)}

        <div className="flex flex-col gap-4">
          {passwordInput(t("password.new"), "newPassword", showNew, setShowNew)}
          {passwordInput(t("password.confirm"), "confirmPassword", showConfirm, setShowConfirm)}

          <div className="flex flex-col gap-2">
            {criterion(t("password.minLength"), isLengthValid)}
            {criterion(t("password.hasNumber"), hasNumber)}
            {criterion(t("password.hasCapital"), hasCapital)}
            {criterion(t("password.matches"), passwordsMatch)}
          </div>
        </div>

        <AppButton
          className="w-fit"
          type="submit"
          disabled={!isValid || isPending}
          isLoading={isPending}
          loadingText={t("common.saving")}
        >
          {t("common.save")}
        </AppButton>
      </form>
    </div>
  );
}
